import { useEffect, useState } from 'react'

import MainLoader from 'ageas-react-component-loader'
import { useWarningStore } from '../../Stores/warningStore'

import { MainContainer } from '../../GlobalStyles'
import { useSimulatorStore } from '../../Stores/simulatorStore'
import { useEntityDetailsForSummaryDocumentStore } from '../../Stores/useEntityDetailsForSummaryDocumentStore'

import { MatchedEntity } from '../../@types/PolicyHolderDetails'
import { useReportStore } from '../../Stores/reportStore'
import { STATUS_REQUEST } from '../../constants/statusRequest'
import { WARNING_BAR } from '../../constants/warningBar'
import { useGetEntityDetailsForSummaryDocument } from '../../hooks/api/useGetEntityDetailsForSummaryDocument'
import { useValidateNif } from '../../hooks/api/useValidateNif'
import { useErrorRequest } from '../../hooks/useErrorRequest'
import { t } from '../../i18n'
import { validateNIF } from '../../utils/validates'
import { NIF } from '../NIF'
import { EntityTableSelection } from '../SelectedEntity/EntityTableSelection'
import { Container } from '../SelectedEntity/styles'
import { Report } from './Identification'
import { OwnerContainer } from './styles'

type Props = {
  report: Partial<Report>
  isRerenderReport?: boolean
  updateReport: (report: Partial<Report>) => void
  onChange?: (value: string) => void
  setIsGlobalEntity?: (value: boolean) => void
}

export const ReportOwnerIdentification = ({
  report,
  isRerenderReport,
  updateReport,
  onChange,
  setIsGlobalEntity,
}: Props) => {
  const { handleErrorRequest } = useErrorRequest()
  const [fiscalNumberMessage, setFiscalNumberMessage] = useState<string | null>(
    null,
  )
  const [pristineNif, setPristineNif] = useState(false)
  const { setIsProspect } = useReportStore()
  const { entitySelected, setOwnerFiscalNumber, ownerFiscalNumber } =
    useSimulatorStore()
  const { setIsNifSearched } = useReportStore()
  const {
    setEntityDetailsForSummaryDocumentSelected,
    entityDetailsForSummaryDocumentSelected,
  } = useEntityDetailsForSummaryDocumentStore()

  const [vatNumber, setVatNumber] = useState(
    ownerFiscalNumber || entitySelected?.vatNumber || '',
  )
  const [entityDetailGetRequest, setEntityDetailGetRequest] = useState(
    Boolean(entitySelected?.vatNumber),
  )
  const [entityDetailProspectGetRequest, setEntityDetailProspectGetRequest] =
    useState(Boolean(ownerFiscalNumber) && !Boolean(entitySelected?.vatNumber))
  const { setWarningOptions } = useWarningStore()
  const [showSelectedEntity, setShowSelectedEntity] = useState(false)
  const currentVatNumber =
    entitySelected?.vatNumber ||
    entityDetailsForSummaryDocumentSelected?.vatNumber ||
    vatNumber ||
    ownerFiscalNumber

  const {
    mutate: mutateValidateNif,
    isSuccess: isSuccessValidateNif,
    isLoading: isLoadingValidateNif,
    isError: isErrorValidateNif,
  } = useValidateNif()

  const { data, isFetching, isError, error, isSuccess } =
    useGetEntityDetailsForSummaryDocument(
      {
        vatNumber: vatNumber || null,
      },
      entityDetailGetRequest,
    )

  const entityDetails: MatchedEntity[] =
    data?.matchedIndividualEntities as never

  const handleResetStatesNif = () => {
    setIsNifSearched(false)
    setOwnerFiscalNumber('')
    setFiscalNumberMessage('')
    onChange && onChange('')
    setIsProspect(false)
  }

  useEffect(() => {
    if (isSuccess && data && !isRerenderReport) {
      setIsGlobalEntity && setIsGlobalEntity(true)
      setOwnerFiscalNumber(vatNumber || '')
    }

    if (data === null) {
      setFiscalNumberMessage(t('pages.step1.errors.notFoundEntity'))
      setIsProspect(true)
    }
  }, [isSuccess, data, vatNumber, isRerenderReport])

  useEffect(() => {
    if (data) {
      const entityPreSelected = data?.matchedIndividualEntities.find(
        (item) => item.idEntity === entitySelected?.idEntity,
      )

      setIsProspect(false)

      if (entityPreSelected?.idEntity?.length) {
        setEntityDetailsForSummaryDocumentSelected(entityPreSelected!)
      } else if (data.matchedIndividualEntities.length === 1) {
        setEntityDetailsForSummaryDocumentSelected(
          data.matchedIndividualEntities[0],
        )
        updateReport({
          email: data.matchedIndividualEntities[0].email,
          name: data.matchedIndividualEntities[0].name,
          phone: data.matchedIndividualEntities[0].phone,
        })
      } else {
        setShowSelectedEntity(true)
      }
    }
  }, [data, entitySelected?.name, setEntityDetailsForSummaryDocumentSelected])

  useEffect(() => {
    const _error: any = error

    if (isError && _error) {
      if (_error?.response?.status === STATUS_REQUEST.notFound) {
        setOwnerFiscalNumber(
          JSON.parse(_error?.response?.config?.data).vatNumber || '',
        )
        setFiscalNumberMessage(t('pages.step1.errors.notFoundEntity'))
        setIsProspect(true)
        updateReport({
          email: report.email || '',
          name: report.name || '',
          phone: report.phone || '',
          phoneList: report.phoneList || [],
        })

        return
      }

      if (
        Array.isArray(_error?.response?.data) &&
        (_error?.response?.data || [])?.filter(
          (err: any) => err?.errorCode === 'ERC362',
        ).length
      ) {
        setFiscalNumberMessage(t('general.errors.fiscalNumberInvalid'))

        return
      }

      handleErrorRequest({
        error,
      })
    }
  }, [error, handleErrorRequest, isError])

  useEffect(() => {
    if (pristineNif) {
      setEntityDetailGetRequest(isFetching)
    }
  }, [isFetching, pristineNif])

  useEffect(() => {
    if (
      Boolean(entitySelected) ||
      Boolean(entityDetailsForSummaryDocumentSelected)
    ) {
      setIsNifSearched && setIsNifSearched(true)
    }
  }, [entityDetailsForSummaryDocumentSelected, entitySelected])

  useEffect(() => {
    if (isSuccessValidateNif) {
      setFiscalNumberMessage(null)
      setEntityDetailGetRequest(true)
    }
  }, [isSuccessValidateNif])

  useEffect(() => {
    if (isErrorValidateNif) {
      handleResetStatesNif()

      const nifRefInput: any = document?.querySelector(`#nifRefInput input`)
      nifRefInput!.value = ''
    }
  }, [isErrorValidateNif])

  useEffect(() => {
    if (entityDetailProspectGetRequest) {
      mutateValidateNif({ vatNumber: vatNumber || '' })
      setEntityDetailProspectGetRequest(false)
    }
  }, [vatNumber])

  return (
    <>
      <OwnerContainer>
        <MainLoader show={isFetching || isLoadingValidateNif} />
        <div className="row">
          <div id="nifRefInput">
            <NIF
              required
              value={currentVatNumber}
              label={t('pages.step2.generateReport.vatNumber')}
              errorMessage={fiscalNumberMessage}
              onClick={() => {
                setIsNifSearched && setIsNifSearched(true)

                if (validateNIF(vatNumber)) {
                  mutateValidateNif({ vatNumber: vatNumber || '' })
                } else {
                  setFiscalNumberMessage(
                    t('general.errors.fiscalNumberInvalid'),
                  )
                  setEntityDetailGetRequest(false)
                }
              }}
              readOnly={
                Boolean(entitySelected) ||
                Boolean(entityDetailsForSummaryDocumentSelected)
              }
              onValueChanged={(value: string) => {
                if ((value || '').length > 0) {
                  setPristineNif(true)
                  if (validateNIF(value)) {
                    onChange && onChange(value)
                    setFiscalNumberMessage(t('general.search'))
                    setWarningOptions({
                      labelTypes: [],
                      mode: WARNING_BAR.mode.hide,
                    })
                  } else {
                    setIsNifSearched(false)
                    setEntityDetailGetRequest(false)
                    setFiscalNumberMessage(
                      t('general.errors.fiscalNumberInvalid'),
                    )
                  }
                } else {
                  handleResetStatesNif()
                }
                setVatNumber(value)
              }}
            />
          </div>
          {(data?.matchedIndividualEntities || []).length > 1 &&
            showSelectedEntity && (
              <MainContainer>
                <Container>
                  <EntityTableSelection
                    data={entityDetails}
                    type="entityForSummaryDocument"
                    closeAction={() => setShowSelectedEntity(false)}
                  />
                </Container>
              </MainContainer>
            )}
        </div>
      </OwnerContainer>
    </>
  )
}
